'use client';

import * as React from 'react';
import { cn } from '@/shared/utils/styling';
import ClearIcon from '@/shared/assets/clear-icon.svg?react';
import { tv } from 'tailwind-variants';

const textareaVariants = tv({
  base: 'flex w-full rounded-2xl px-[1.6rem] text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 bg-muted focus:bg-stroke aria-invalid:bg-light-error resize-none h-[6rem] min-h-[6rem] flex items-start break-all',
  variants: {
    shouldFloat: {
      true: 'pt-[2.4rem] pb-2',
      false: 'py-4',
    },
    hasIcon: {
      true: 'pl-[4.2rem]',
      false: 'px-6',
    },
  },
});

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  showClearButton?: boolean;
  onClear?: () => void;
  label?: string;
  showLabel?: boolean;
  icon?: React.ReactNode;
  rightContent?: React.ReactNode;
  autoGrow?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      showClearButton = true,
      onClear,
      value,
      onChange,
      label,
      placeholder,
      showLabel = true,
      icon,
      rightContent,
      autoGrow = false,
      ...props
    },
    ref
  ) => {
    const [textareaValue, setTextareaValue] = React.useState(value || '');
    const [isFocused, setIsFocused] = React.useState(false);
    const [rightPadding, setRightPadding] = React.useState(0);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const adjustHeight = React.useCallback(() => {
      if (!autoGrow || !textareaRef.current) return;

      requestAnimationFrame(() => {
        if (!textareaRef.current) return;

        const textarea = textareaRef.current;
        textarea.style.height = '';
        textarea.style.height = `${textarea.scrollHeight}px`;
      });
    }, [autoGrow]);

    React.useEffect(() => {
      setTextareaValue(value || '');
    }, [value]);

    React.useEffect(() => {
      adjustHeight();
    }, [adjustHeight, textareaValue, rightPadding]);

    // Use a more reliable focus detection with proper timing
    React.useEffect(() => {
      const checkFocus = () => {
        if (textareaRef.current) {
          setIsFocused(document.activeElement === textareaRef.current);
        }
      };

      // Check focus on mount
      checkFocus();

      // Listen for focus changes on the document with proper timing
      const handleFocusIn = () => {
        setTimeout(checkFocus, 0);
      };

      const handleFocusOut = () => {
        setTimeout(checkFocus, 0);
      };

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const handleTextareaChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTextareaValue(e.target.value);
        onChange?.(e);

        adjustHeight();
      },
      [onChange, autoGrow, adjustHeight]
    );

    const handleRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && node) {
          (ref as React.MutableRefObject<HTMLTextAreaElement>).current = node;
        }
        if (node) {
          textareaRef.current = node;
        }
      },
      [ref]
    );

    const handleClear = React.useCallback(() => {
      setTextareaValue('');
      onClear?.();
      // Create a synthetic event for onChange
      const syntheticEvent = {
        target: { value: '' },
        currentTarget: { value: '' },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onChange?.(syntheticEvent);
    }, [onChange, onClear]);

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        props.onFocus?.(e);
      },
      [props]
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        props.onBlur?.(e);
      },
      [props]
    );

    // Add onInput handler for real-time auto-grow
    const handleInput = React.useCallback(
      (e: React.FormEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        props.onInput?.(e);
      },
      [autoGrow, props]
    );

    const shouldFloat =
      showLabel && (isFocused || (textareaValue as string).length > 0);
    const labelText = label || placeholder;

    // Calculate right padding based on buttons
    const hasClearButton = showClearButton && isFocused;

    const calculateRightPadding = React.useCallback(() => {
      if (!textareaRef.current) return 0;

      const rightContainer = textareaRef.current.parentElement?.querySelector(
        '[data-right-container]'
      );
      if (!rightContainer) return 0;

      const containerWidth = rightContainer.getBoundingClientRect().width;

      return containerWidth + 12; // 0.8rem = 12px
    }, [rightContent, hasClearButton]);

    React.useEffect(() => {
      setRightPadding(calculateRightPadding());
      adjustHeight();
    }, [calculateRightPadding, rightContent, hasClearButton]);

    const textareaClasses = textareaVariants({
      shouldFloat,
      hasIcon: !!icon,
    });

    return (
      <div className="relative w-full">
        {showLabel && labelText && (
          <label
            className={cn(
              'absolute left-6 transition-all duration-200 ease-in-out pointer-events-none',
              'transition-all duration-200 ease-in-out pointer-events-none text-muted-foreground p3 top-1/2 -translate-y-1/2 text-base text-muted-foreground',
              shouldFloat && 'top-[7px] translate-none p6'
            )}>
            {labelText}
          </label>
        )}

        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            {icon}
          </div>
        )}

        <textarea
          className={cn(textareaClasses, className)}
          style={{
            paddingRight: `${rightPadding}px`,
            ...(autoGrow && { overflow: 'hidden' }),
          }}
          ref={handleRef}
          value={textareaValue}
          {...props}
          onChange={handleTextareaChange}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        <div
          className={cn(
            'absolute top-0 right-[2rem] h-full flex items-center gap-[2.4rem] text-secondary',
            rightContent ? 'right-[0.8rem]' : ''
          )}
          data-right-container>
          {hasClearButton && (
            <div
              onMouseDown={e => {
                // Prevent textarea from losing focus
                e.preventDefault();
              }}
              onClick={handleClear}
              className="cursor-pointer">
              <ClearIcon className="size-10" />
            </div>
          )}
          {rightContent}
        </div>
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
